'use client';

import * as React from 'react';
import Link from 'next/link';
import { cultivPath } from '../site';
import type { CultivCopy } from '../types';

/* Self-built cookie banner for the marketing site — accept all / reject all,
   stored in a first-party cookie. It also queues Google Consent Mode v2 signals
   on dataLayer: no tag reads them yet, but the future GTM/pixel integration
   inherits a correct consent state without touching this component. */

const CONSENT_COOKIE = 'cultiv-consent';
const GRANT_DAYS = 365;
/* Re-ask sooner after a refusal (Belgian DPA guidance: renew refused consent periodically). */
const DENY_DAYS = 180;

type Consent = 'granted' | 'denied';

function readConsent(): Consent | null {
  const m = document.cookie.match(/(?:^|;\s*)cultiv-consent=(granted|denied)/);
  return (m?.[1] as Consent) ?? null;
}

function writeConsent(value: Consent) {
  const days = value === 'granted' ? GRANT_DAYS : DENY_DAYS;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${secure}`;
}

/* gtag must push the live `arguments` object — GTM's consent queue rejects plain arrays. */
function gtag(..._args: unknown[]) {
  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

function pushConsent(mode: 'default' | 'update', value: Consent) {
  gtag('consent', mode, {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
}

export function ConsentBanner({ copy, locale }: { copy: CultivCopy; locale: string }) {
  const t = copy.common;
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const stored = readConsent();
    pushConsent('default', 'denied');
    if (stored === 'granted') pushConsent('update', 'granted');
    if (!stored) setOpen(true);
  }, []);

  if (!open) return null;

  const choose = (value: Consent) => {
    writeConsent(value);
    pushConsent('update', value);
    setOpen(false);
  };

  return (
    <section className="b-consent" aria-label={t.consentAria}>
      <button type="button" className="x" aria-label={t.ariaClose} onClick={() => choose('granted')}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
      <span className="b-v2-kick">{t.consentTitle}</span>
      <p>
        {t.consentText}{' '}
        <Link href={cultivPath(locale, '/privacy-policy')}>{t.consentPrivacy}</Link>
      </p>
      <div className="row">
        <button type="button" className="b-btn orange" onClick={() => choose('granted')}>
          {t.consentAccept}
        </button>
        <button type="button" className="b-btn ghost" onClick={() => choose('denied')}>
          {t.consentReject}
        </button>
      </div>
    </section>
  );
}
